<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import AutoComplete from "primevue/autocomplete";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Checkbox from "primevue/checkbox";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";

import { uploadImage, getGalleryImages } from "@/shared/api/files.service";
import { useAppToast } from "@/shared/lib/useAppToast";
import { getAllProductTypes } from "@/modules/admin/products/product-types/api/productTypes.service";
import {
  createProduct,
  updateProduct,
} from "@/modules/admin/products/products/api/products.service";
import { getResolvedProductById } from "@/modules/catalog/api/catalog.service";
import { getAttributesByType } from "@/modules/admin/products/attributes/api/attributes.service";
const attributes = ref([]);
const loadingAttributes = ref(false);
const loadingExistingProduct = ref(false);

const route = useRoute();
const router = useRouter();
const toast = useAppToast();
const productId = computed(() => {
  const id = Number(route.params.id);
  return Number.isInteger(id) && id > 0 ? id : null;
});

const isEditMode = computed(() => Boolean(productId.value));

const productTypes = ref([]);
const filteredProductTypes = ref([]);
const productTypeSelected = ref(null);
const loadingProductTypes = ref(false);

const articul = ref("");
const quantity = ref(0);
const basePrice = ref(0);
const salePrice = ref(null);
const saleIsActive = ref(false);
const submitting = ref(false);
const isDefault = ref(false);

const images = ref([
  { id: null, url: "", isDefault: true, uploading: false },
]);

const fileInput = ref(null);
const uploadingCount = ref(0);
const galleryVisible = ref(false);
const galleryImages = ref([]);
const pendingImageIndex = ref(null);
const productTypeOptions = computed(() =>
  productTypes.value.map((productType) => ({
    label: productType.name,
    value: productType.id,
  })),
);

async function loadAttributesForType(typeId) {
  try {
    loadingAttributes.value = true;

    const result = await getAttributesByType(typeId);
    const attributeMap = new Map();

    result.forEach((item) => {
      const name = item.attributeName ?? item.name ?? "Без названия";
      const attributeId =
        item.attributeId ??
        item.productAttributeId ??
        item.characteristicId ??
        name;

      const key = `${attributeId}:${name}`;

      if (!attributeMap.has(key)) {
        attributeMap.set(key, {
          id: attributeId,
          name,
          values: [],
          selectedValue: null,
          filteredValues: [],
        });
      }

      attributeMap.get(key).values.push({
        id: item.valueId ?? item.id,
        value: item.value,
      });
    });

    attributes.value = Array.from(attributeMap.values()).map((attribute) => ({
      ...attribute,
      filteredValues: [...attribute.values],
    }));
  } catch (error) {
    attributes.value = [];
    toast.error(error.message || "Ошибка при загрузке характеристик");
  } finally {
    loadingAttributes.value = false;
  }
}

watch(productTypeSelected, async (selectedProductType) => {
  if (loadingExistingProduct.value) return;
  const typeId =
    selectedProductType && typeof selectedProductType === "object"
      ? Number(selectedProductType.value)
      : null;

  if (!typeId) {
    attributes.value = [];
    return;
  }

  await loadAttributesForType(typeId);
});

function filterAttributeValues(event, attribute) {
  const query = (event.query ?? "").trim().toLowerCase();

  attribute.filteredValues = query
    ? attribute.values.filter((item) =>
        item.value.toLowerCase().includes(query),
      )
    : [...attribute.values];
}

function filterProductTypes(event) {
  const query = (event.query ?? "").trim().toLowerCase();

  filteredProductTypes.value = query
    ? productTypeOptions.value.filter((option) =>
        option.label.toLowerCase().includes(query),
      )
    : [...productTypeOptions.value];
}

function addImageFieldByUrl() {
  images.value.push({
    id: null,
    url: "",
    isDefault: false,
    uploading: false,
  });
}

function triggerFileUpload() {
  fileInput.value?.click();
}

async function uploadFile(file, index) {
  uploadingCount.value += 1;

  try {
    const response = await uploadImage(file, "products");

    if (images.value[index]) {
      images.value[index].url = response.url;
      images.value[index].storageKey = response.storageKey;
      images.value[index].uploading = false;
    }
  } catch (error) {
    if (images.value[index]) {
      images.value[index].uploading = false;
    }

    toast.error(
      error.response?.data?.message ||
        error.message ||
        "Не удалось загрузить изображение",
    );

    throw error;
  } finally {
    uploadingCount.value -= 1;
  }
}

async function onFilesSelected(event) {
  const files = Array.from(event.target.files ?? []);

  for (const file of files) {
    images.value.push({
      id: null,
      url: "",
      isDefault: images.value.every((image) => !image.url),
      uploading: true,
    });

    const index = images.value.length - 1;

    try {
      await uploadFile(file, index);
    } catch {
      if (images.value[index] && !images.value[index].url) {
        images.value.splice(index, 1);
      }
    }
  }

  if (fileInput.value) {
    fileInput.value.value = "";
  }
}

async function openGallery(index = null) {
  try {
    galleryImages.value = await getGalleryImages("products");
    pendingImageIndex.value = index;
    galleryVisible.value = true;
  } catch (error) {
    galleryImages.value = [];
    toast.error(error.message || "Ошибка загрузки галереи");
  }
}

function selectFromGallery(url) {
  if (pendingImageIndex.value !== null && images.value[pendingImageIndex.value]) {
    images.value[pendingImageIndex.value].url = url;
  } else {
    const emptyIndex = images.value.findIndex(
      (image) => !image.url && !image.uploading,
    );

    if (emptyIndex >= 0) {
      images.value[emptyIndex].url = url;
    } else {
      images.value.push({
        id: null,
        url,
        isDefault: images.value.every((image) => !image.url),
        uploading: false,
      });
    }
  }

  pendingImageIndex.value = null;
  galleryVisible.value = false;
}

function setDefaultImage(index) {
  images.value = images.value.map((image, imageIndex) => ({
    ...image,
    isDefault: imageIndex === index,
  }));
}

function removeImage(index) {
  if (images.value.length === 1) {
    images.value[0] = {
      id: null,
      url: "",
      isDefault: true,
      uploading: false,
    };
    return;
  }

  const removedWasDefault = images.value[index]?.isDefault;
  images.value.splice(index, 1);

  if (removedWasDefault && images.value.length) {
    images.value[0].isDefault = true;
  }
}

async function submitProduct() {
  if (submitting.value || uploadingCount.value > 0) return;

  const productTypeId =
    productTypeSelected.value &&
    typeof productTypeSelected.value === "object"
      ? Number(productTypeSelected.value.value)
      : null;

  const validImages = images.value
    .map((image) => ({
      id: image.id ?? null,
      url: (image.storageKey || image.url).trim(),
      isDefault: image.isDefault,
    }))
    .filter((image) => image.url);

  if (!productTypeId) {
    toast.error("Выберите основной товар");
    return;
  }

  if (!articul.value.trim()) {
    toast.error("Введите артикул");
    return;
  }

  if (Number(basePrice.value) <= 0) {
    toast.error("Базовая цена должна быть больше нуля");
    return;
  }

  if (
    saleIsActive.value &&
    (!salePrice.value || Number(salePrice.value) >= Number(basePrice.value))
  ) {
    toast.error("Цена со скидкой должна быть меньше базовой цены");
    return;
  }

  if (validImages.length === 0 && isEditMode.value && isDefault.value) {
    toast.error("Добавьте хотя бы одно изображение");
    return;
  }

  if (validImages.length > 0 && validImages.filter((image) => image.isDefault).length !== 1) {
    toast.error("Выберите ровно одно главное изображение");
    return;
  }

  try {
    submitting.value = true;

    const payload = {
  articul: articul.value.trim(),
  quantity: Number(quantity.value ?? 0),
  basePrice: Number(basePrice.value),
  salePrice:
    salePrice.value === null || salePrice.value === ""
      ? null
      : Number(salePrice.value),
  saleIsActive: saleIsActive.value,
  productTypeId,
  images: validImages,
  characteristicValues: attributes.value
    .filter((attribute) => attribute.selectedValue?.id)
    .map((attribute) => ({
      valueId: Number(attribute.selectedValue.id),
    })),
};

if (isEditMode.value) {
  payload.isDefault = isDefault.value ? true : null;
}

let savedProductId = productId.value;

if (isEditMode.value) {
  await updateProduct(productId.value, payload);
  toast.success("Товар обновлён");
} else {
  const result = await createProduct(payload);
  savedProductId = Number(result?.id ?? result?.value ?? result);

  if (!Number.isInteger(savedProductId) || savedProductId <= 0) {
    throw new Error("Сервер не вернул ID созданного товара");
  }

  toast.success("Товар создан");
}

router.push({
  name: "adminProductDetails",
  params: { id: String(savedProductId) },
});
  } catch (error) {
    toast.error(
      error.response?.data?.message ||
        error.message ||
        "Ошибка при создании товара",
    );
  } finally {
    submitting.value = false;
  }
}

function goBack() {
  router.push({ name: "adminProducts" });
}

async function loadExistingProduct() {
  if (!productId.value) return;

  try {
    loadingExistingProduct.value = true;

    const product = await getResolvedProductById(productId.value);

    articul.value = product.articul ?? "";
    quantity.value = Number(product.quantity ?? 0);
    basePrice.value = Number(product.basePrice ?? 0);
    salePrice.value = product.salePrice ?? null;
    saleIsActive.value = Boolean(product.saleIsActive);
    isDefault.value = Boolean(product.isDefault);

    images.value =
      Array.isArray(product.images) && product.images.length
        ? product.images.map((image) => ({
            id: image.id ?? null,
            url: image.url ?? "",
            storageKey: image.storageKey ?? null,
            isDefault: Boolean(image.isDefault),
            uploading: false,
          }))
        : [{ id: null, url: "", isDefault: true, uploading: false }];

    const typeId = Number(
      product.productTypeId ?? product.productType?.id,
    );

    const matchedType = productTypes.value.find(
      (type) => Number(type.id) === typeId,
    );

    productTypeSelected.value = matchedType
      ? { label: matchedType.name, value: matchedType.id }
      : null;

    if (typeId) {
      await loadAttributesForType(typeId);

      const selectedValueIds = new Set();

      if (Array.isArray(product.characteristicValues)) {
        product.characteristicValues.forEach((item) => {
          const valueId = Number(
            item.valueId ??
              item.characteristicValueId ??
              item.id,
          );

          if (Number.isInteger(valueId) && valueId > 0) {
            selectedValueIds.add(valueId);
          }
        });
      }

      if (Array.isArray(product.attributes)) {
        product.attributes.forEach((attribute) => {
          const valueId = Number(
            attribute.selectedValueId ??
              attribute.selectedValue?.id,
          );

          if (Number.isInteger(valueId) && valueId > 0) {
            selectedValueIds.add(valueId);
          }
        });
      }

      attributes.value.forEach((attribute) => {
        attribute.selectedValue =
          attribute.values.find((value) =>
            selectedValueIds.has(Number(value.id)),
          ) ?? null;
      });
    }
  } catch (error) {
    toast.error(error.message || "Ошибка загрузки товара");
  } finally {
    loadingExistingProduct.value = false;
  }
}

async function loadProductTypes() {
  try {
    loadingProductTypes.value = true;
    productTypes.value = await getAllProductTypes();
  } catch (error) {
    productTypes.value = [];
    toast.error(error.message || "Ошибка при загрузке основных товаров");
  } finally {
    loadingProductTypes.value = false;
  }
}

onMounted(async () => {
  await loadProductTypes();

  if (isEditMode.value) {
    await loadExistingProduct();
  }
});
</script>

<template>
  <div class="admin-product-editor-page">
    <div class="page-shell">
      <div class="page-header">
        <div>
          <Button
            label="Назад к товарам"
            icon="pi pi-arrow-left"
            severity="secondary"
            text
            class="back-button"
            @click="goBack"
          />

          <h1>{{ isEditMode ? "Редактирование товара" : "Создание товара" }}</h1>
<p>
  {{
    isEditMode
      ? "Измените данные товарной позиции."
      : "Заполните основные данные товарной позиции."
  }}
</p>
        </div>
      </div>

      <section class="editor-section">
        <div class="section-header">
          <h2>Основные данные</h2>
        </div>

        <div class="form-grid">
          <div class="field field-wide">
            <label for="product-type">Основной товар</label>
            <AutoComplete
              id="product-type"
              v-model="productTypeSelected"
              :suggestions="filteredProductTypes"
              optionLabel="label"
              placeholder="Введите название основного товара"
              :loading="loadingProductTypes"
              dropdown
              completeOnFocus
              class="w-full"
              @complete="filterProductTypes"
            />
          </div>

          <div class="field">
            <label for="articul">Артикул</label>
            <InputText
              id="articul"
              v-model="articul"
              placeholder="Например, RV605CR"
              class="w-full"
            />
          </div>

          <div class="field">
            <label for="quantity">Количество</label>
            <InputNumber
              id="quantity"
              v-model="quantity"
              :min="0"
              class="w-full"
            />
          </div>

          <div class="field">
            <label for="base-price">Базовая цена, €</label>
            <InputNumber
              id="base-price"
              v-model="basePrice"
              :min="0"
              mode="decimal"
              class="w-full"
            />
          </div>

          <div class="field">
            <label for="sale-price">Цена со скидкой, €</label>
            <InputNumber
              id="sale-price"
              v-model="salePrice"
              :min="0"
              mode="decimal"
              class="w-full"
            />
          </div>
        </div>

        <div class="checkbox-field">
          <Checkbox
            v-model="saleIsActive"
            inputId="sale-active"
            binary
          />
          <label for="sale-active">Скидка активна</label>
        </div>
        <div v-if="isEditMode" class="checkbox-field">
        <Checkbox
          v-model="isDefault"
          inputId="product-default"
          binary
          :disabled="isDefault"
        />
        <label for="product-default">
          {{ isDefault ? "Товар по умолчанию" : "Сделать товаром по умолчанию" }}
        </label>
      </div>
      </section>
      <section
  v-if="loadingAttributes || attributes.length"
  class="editor-section attributes-section"
>
  <div class="section-header">
    <h2>Характеристики</h2>
  </div>

  <div v-if="loadingAttributes" class="attributes-loading">
    <i class="pi pi-spin pi-spinner"></i>
    Загрузка характеристик...
  </div>

  <div v-else class="form-grid">
    <div
      v-for="attribute in attributes"
      :key="attribute.id"
      class="field"
    >
      <label>{{ attribute.name }}</label>

      <AutoComplete
        v-model="attribute.selectedValue"
        :suggestions="attribute.filteredValues"
        optionLabel="value"
        placeholder="Выберите значение"
        dropdown
        completeOnFocus
        class="w-full"
        @complete="(event) => filterAttributeValues(event, attribute)"
      />
    </div>
  </div>
</section>
      <section class="editor-section images-section">
  <div class="section-header images-header">
    <div>
      <h2>Изображения</h2>
      <p>Добавьте минимум одно изображение товара.</p>
    </div>

    <div class="images-actions">
      <Button
        label="По URL"
        icon="pi pi-link"
        severity="secondary"
        outlined
        @click="addImageFieldByUrl"
      />
      <Button
        label="Загрузить файл"
        icon="pi pi-upload"
        severity="secondary"
        outlined
        @click="triggerFileUpload"
      />
      <Button
        label="Из галереи"
        icon="pi pi-images"
        severity="secondary"
        outlined
        @click="openGallery()"
      />
    </div>
  </div>

  <input
    ref="fileInput"
    type="file"
    accept="image/*"
    multiple
    style="display: none"
    @change="onFilesSelected"
  />

  <div class="image-list">
    <div
      v-for="(image, index) in images"
      :key="image.id ?? index"
      class="image-row"
    >
      <InputText
        v-model="image.url"
        placeholder="URL изображения"
        class="image-url-input"
        :disabled="image.uploading"
      />

      <Button
        icon="pi pi-images"
        severity="secondary"
        outlined
        title="Выбрать из галереи"
        :disabled="image.uploading"
        @click="openGallery(index)"
      />

      <Button
        :label="image.isDefault ? 'Главное' : 'Сделать главным'"
        severity="secondary"
        :outlined="!image.isDefault"
        :disabled="image.uploading"
        @click="setDefaultImage(index)"
      />

      <Button
        icon="pi pi-trash"
        severity="danger"
        outlined
        :disabled="image.uploading"
        @click="removeImage(index)"
      />
    </div>
  </div>

  <div v-if="uploadingCount > 0" class="uploading-status">
    <i class="pi pi-spin pi-spinner"></i>
    Загружаются изображения: {{ uploadingCount }}
  </div>
</section>

<div class="editor-actions">
  <Button
    label="Отмена"
    severity="secondary"
    outlined
    :disabled="submitting"
    @click="goBack"
  />

  <Button
    :label="isEditMode ? 'Сохранить изменения' : 'Создать товар'"
    icon="pi pi-check"
    :loading="submitting"
    :disabled="submitting || uploadingCount > 0"
    @click="submitProduct"
  />
</div>

<Dialog
  v-model:visible="galleryVisible"
  header="Галерея изображений"
  modal
  :style="{ width: 'min(850px, 95vw)' }"
>
  <div class="gallery-grid">
    <button
      v-for="image in galleryImages"
      :key="image.url"
      type="button"
      class="gallery-item"
      @click="selectFromGallery(image.url)"
    >
      <img :src="image.url" :alt="image.name" />
      <span>{{ image.name }}</span>
    </button>

    <div v-if="galleryImages.length === 0" class="gallery-empty">
      В галерее пока нет изображений
    </div>
  </div>
</Dialog>
    </div>
  </div>
</template>

<style scoped>
.admin-product-editor-page {
  min-height: 100vh;
  background: #f7f7f7;
}

.page-shell {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.page-header {
  margin-bottom: 24px;
}

.back-button {
  margin-bottom: 12px;
  padding-left: 0;
}

.page-header h1 {
  margin: 0;
  font-size: 32px;
}

.page-header p {
  margin: 8px 0 0;
  color: #666;
}

.editor-section {
  padding: 24px;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  background: #fff;
}

.section-header {
  margin-bottom: 20px;
}

.section-header h2 {
  margin: 0;
  font-size: 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-wide {
  grid-column: 1 / -1;
}

.field label {
  font-weight: 600;
}

.checkbox-field {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
}

.w-full {
  width: 100%;
}

.images-section {
  margin-top: 24px;
}

.images-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.images-header p {
  margin: 6px 0 0;
  color: #666;
}

.images-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.hidden-file-input {
  display: none;
}

.image-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.image-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #fafafa;
}

.image-url-input {
  flex: 1;
  min-width: 240px;
}

.uploading-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  color: #1976d2;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  max-height: 520px;
  overflow-y: auto;
}

.gallery-item {
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #fff;
  text-align: left;
}

.gallery-item:hover {
  border-color: #c41217;
}

.gallery-item img {
  display: block;
  width: 100%;
  height: 130px;
  object-fit: cover;
}

.gallery-item span {
  display: block;
  padding: 8px;
  font-size: 12px;
  word-break: break-word;
}

.gallery-empty {
  grid-column: 1 / -1;
  padding: 32px;
  color: #777;
  text-align: center;
}

.editor-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

@media (max-width: 700px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .field-wide {
    grid-column: auto;
  }
}
</style>
